import moment from 'moment'
import { IDynamicTimelineItem } from '../../../interfaces/IDynamicTimeline'
import { IDynamicBaseStruct } from '../../../interfaces/IDynamicFields'
import { ApplicationCheckoutTypeArray } from '../../../interfaces/IAdoptionApplicationCheckout'
import { PetStatusArray } from '../../../interfaces/IPet'
import IIntegrationSyncSection from '../../../interfaces/IIntegrationSyncSection'
import { PartnerKeyType } from '../../../interfaces/IIntegration'
import { ISettingsCustomFields } from '../../../interfaces/ISettingsCustomFields'

interface CredentialsFields {
  partner_key: PartnerKeyType,
  endpoint: string,
  fields: Record<any, {
    label: string,
    description?: string,
    required?: boolean
  }>
}

export namespace IntegrationConstants {
  /**
   * Sync timeline fields
   */
  export const SYNC_TIMELINE: Record<any, IDynamicTimelineItem> = {
    created_date: {
      order: 1,
      label: 'Started at',
      render: (value?: string) => `${value ? moment(value).format('YYYY/MM/DD HH:mm') : ''}`
    },
    total: {
      order: 2,
      label: 'Total',
      render: (value: number) => `${value} pets`
    },
    filtered: {
      order: 3,
      label: 'New',
      render: (value: number) => `${value} pets`
    },
    updated_pets: {
      order: 4,
      label: 'Updated',
      render: (value: number) => `${value} pets`
    },
    migrated_pets: {
      order: 5,
      label: 'Migrated',
      render: (value: number) => `${value} pets`
    },
    restored_pets: {
      order: 6,
      label: 'Restored',
      render: (value: number) => `${value} pets`
    },
    archived_pets: {
      order: 7,
      label: 'Archived',
      render: (value: number) => `${value} pets`
    },
    fails: {
      order: 8,
      label: 'Failed',
      render: (value: number) => `${value} pets`
    },
    status: {
      order: 9,
      custom: true,
      label: '',
      render: (value: number) => `${moment.utc(moment.duration(value, 'milliseconds').asMilliseconds()).format('HH:mm:ss')} elapsed`
    }
  }

  /**
   * Sync sections for tags
   */
  export const SYNC_SECTIONS_TAGS = {
    info: {
      label: 'Info',
      color: 'gold'
    },
    price: {
      label: 'Price',
      color: 'cyan'
    },
    pictures: {
      label: 'Pictures',
      color: 'blue'
    },
    videos: {
      label: 'Videos',
      color: 'orange'
    },
    foster: {
      label: 'Foster',
      color: 'geekblue'
    },
    description: {
      label: 'Description',
      color: 'purple'
    }
  }

  /**
   * Sync sections initial data
   */
  export const SYNC_SECTIONS_INITIAL_DATA: IIntegrationSyncSection[] = [
    {
      uuid: '',
      name: 'INFO',
      status: 'ALWAYS'
    },
    {
      uuid: '',
      name: 'DESCRIPTION',
      status: 'ALWAYS'
    },
    {
      uuid: '',
      name: 'FOSTER',
      status: 'ALWAYS'
    },
    {
      uuid: '',
      name: 'PRICE',
      status: 'ALWAYS'
    },
    {
      uuid: '',
      name: 'PICTURES',
      status: 'ALWAYS'
    },
    {
      uuid: '',
      name: 'VIDEOS',
      status: 'ALWAYS'
    },
    {
      uuid: '',
      name: 'CHARACTERISTICS',
      status: 'ALWAYS'
    },
    {
      uuid: '',
      name: 'BRANCH',
      status: 'ALWAYS'
    }
  ]

  export const FILTER_SETTINGS_STRUCT: IDynamicBaseStruct = {
    filters: {
      title: 'Filters',
      collapsed: true,
      fields: {
        field: {
          label: 'Field',
          type: 'text'
        },
        values: {
          label: 'Values',
          type: 'object',
          fields: {
            text: {
              label: 'Text',
              type: 'text'
            }
          }
        }
      }
    },
    exclude_filters: {
      title: 'Exlude Filters',
      collapsed: true,
      fields: {
        field: {
          label: 'Field',
          type: 'text'
        },
        values: {
          label: 'Values',
          type: 'object',
          fields: {
            text: {
              label: 'Text',
              type: 'text'
            }
          }
        }
      }
    }
  }

  /**
   * Base struct for dynamic fields
   */
  export const BASE_SETTINGS_STRUCT: IDynamicBaseStruct = {
    specie_pricing_map: {
      title: 'Species Pricing Map',
      collapsed: true,
      fields: {
        specie_uuid: {
          label: 'Species',
          type: 'select'
        },
        ages: {
          label: 'Pricing',
          type: 'object',
          fields: {
            age_key: {
              label: 'Age',
              type: 'select'
            },
            price: {
              label: 'Price',
              type: 'number'
            }
          }
        }
      }
    },
    status_map: {
      title: 'Status Map',
      collapsed: true,
      fields: {
        id: {
          label: 'ID',
          type: 'text'
        },
        status: {
          label: 'Status',
          type: 'object',
          fields: {
            type: {
              label: 'Type',
              type: 'select'
            },
            value: {
              label: 'Value',
              type: 'custom'
            }
          }
        }
      }
    }
  }

  /**
   * Custom settings fields 
   */
  export const SETTING_CUSTOM_FIELDS: ISettingsCustomFields[] = [
    {
      partner_key: 'SHELTER_BUDDY',
      fields: {
        payment_method_id: {
          label: 'Payment Method ID',
          type: 'number',
          required: true,
        },
        search_status_list: {
          label: 'Search Status',
          type: 'list',
          fields: [
            {
              label: 'Value',
              name: 'value',
              type: 'number'
            }
          ]
        },
        locations_list: {
          label: 'Locations',
          type: 'list',
          fields: [
            {
              label: 'Value',
              name: 'value',
              type: 'number'
            }
          ]
        },
        search_group_list: {
          label: 'Search Group',
          type: 'list',
          fields: [
            {
              label: 'Value',
              name: 'value',
              type: 'number'
            }
          ]
        },
        acceptable_status_list: {
          label: 'Acceptable Status',
          type: 'list',
          fields: [
            {
              label: 'Value',
              name: 'value',
              type: 'number'
            }
          ]
        },
        receipt_account_map: {
          label: 'Receipt Account Map',
          type: 'list',
          fields: [
            {
              label: 'Checkout Type',
              name: 'map_type',
              type: 'select',
              custom: true,
              options: ApplicationCheckoutTypeArray.map(type => {
                return {
                  label: type,
                  value: type
                }
              }) as any
            },
            {
              label: 'Value',
              name: 'value',
              type: 'text'
            }
          ]
        },
        base_status_map: {
          label: 'Base Status Map',
          type: 'list',
          fields: [
            {
              label: 'Pet Base Status Type',
              name: 'map_type',
              type: 'select',
              custom: true,
              options: PetStatusArray.map(type => {
                return {
                  label: type,
                  value: type
                }
              }) as any
            },
            {
              label: 'Value',
              name: 'value',
              type: 'text'
            }
          ]
        },
        contract_options_map: {
          label: 'Contract Options Map',
          type: 'list',
          fields: [
            {
              label: 'Contract Option',
              name: 'map_type',
              type: 'select'
            },
            {
              label: 'Value',
              name: 'value',
              type: 'text'
            }
          ]
        }
      },
    },
    {
      partner_key: 'SHELTER_LUV',
      fields: {
        status_list: {
          label: 'Status',
          type: 'list',
          fields: [
            {
              label: 'Value',
              name: 'value',
              type: 'text'
            }
          ]
        },
        specie_list: {
          label: 'Search Specie',
          type: 'list',
          fields: [
            {
              label: 'Value',
              name: 'value',
              type: 'text'
            }
          ]
        }
      }
    },
    {
      partner_key: "PETPOINT",
      fields: {
        site: {
          label: "Site",
          type: 'input',
        }
      }
    },
  ]

  /**
   * Credentials fields
   */
  export const CREDENTIALS_FIELDS: CredentialsFields[] = [
    {
      partner_key: 'ANIMALS_FIRST',
      endpoint: 'animals-first/auth',
      fields: {
        key: {
          label: 'Key',
          required: true
        }
      }
    },
    {
      partner_key: 'SHELTER_MANAGER',
      endpoint: 'shelter-manager/auth',
      fields: {
        account: {
          label: 'Account',
          required: true
        },
        username: {
          label: 'Username',
          required: true
        },
        password: {
          label: 'Password',
          required: true
        }
      }
    },
    {
      partner_key: 'CHAMELEON',
      endpoint: 'chameleon/auth',
      fields: {
        url: {
          label: 'URL',
          required: true
        }
      }
    },
    {
      partner_key: 'CHAMELEON_DALLAS',
      endpoint: 'chameleon/auth',
      fields: {
        url: {
          label: 'URL',
          required: true
        }
      }
    },
    {
      partner_key: 'LIFELINE',
      endpoint: 'lifeline/auth',
      fields: {
        url: {
          label: 'URL',
          required: true
        }
      }
    },
    {
      partner_key: 'PETFINDER',
      endpoint: 'petfinder/auth',
      fields: {
        organization_id: {
          label: 'Organization ID',
          required: true
        },
        key: {
          label: 'Key',
          required: true
        },
        secret: {
          label: 'Secret',
          required: true
        }
      }
    },
    {
      partner_key: 'PETPOINT',
      endpoint: 'petpoint/auth',
      fields: {
        key: {
          label: 'Key',
          required: true
        }
      }
    },
    {
      partner_key: 'SHELTER_BUDDY',
      endpoint: 'shelterbuddy/auth',
      fields: {
        url: {
          label: 'Shelter Buddy URL.',
          description: 'This should be the account Base URL of the shelter. Here is an example: https://shelterbuddy-us-uat.shelterbuddy.io/',
          required: true
        },
        username: {
          label: 'Username',
          required: true
        },
        password: {
          label: 'Password',
          required: true
        }
      }
    },
    {
      partner_key: 'SHELTER_LUV',
      endpoint: 'shelterluv/auth',
      fields: {
        key: {
          label: 'Key',
          required: true
        }
      }
    },
  ]
}
